import { useCallback } from "react";
import { Address } from "@blaze-cardano/core";
import { blake2b } from "@noble/hashes/blake2";
import { randomBytes } from "@noble/hashes/utils.js";
import { chacha20poly1305 } from "@noble/ciphers/chacha.js";
import { compressPayload } from "@/lib/payloadCompression";
import { useWalletObserver } from "@sundaeswap/wallet-lite";

import { useAppContext } from "@/context/AppContext";
import { regenerateEphemeralWallet } from "@/lib/ephemeralWallet";
import { useHydrozoaHeadInfo } from "./useHydrozoaScriptAddress";
import { useTransactionWithBody } from "./useTransactionWithBody";

const toSeconds = (value: number) => Math.floor(value / 1000);

/**
 * Recovers from a rejected WS private-subscribe auth by re-establishing the
 * local delegated (ephemeral) signing key on the ledger, signed with the MAIN
 * wallet -- only the account's own key can register a delegate.
 *
 * Wipes the old ephemeral key, generates a fresh one, and submits an
 * `AddDelegatedKey` for it. `baseNonce` is the last known account nonce (0
 * for a never-acted account); the action uses `baseNonce + 1`. This works
 * before we can read the account over the failing authed WS feed: a
 * freshly-deposited account is at nonce 0, and the ledger safely rejects the
 * action with `AccountNotFound` (no panic) if the deposit has not been absorbed
 * yet, so the caller can retry once it is.
 *
 * Deliberately self-contained (no GummiwormContext): it runs inside the
 * provider that owns that context, where the context value is not yet visible.
 */
export function useReDelegateKey(): (baseNonce: number) => Promise<void> {
  const {
    state: { blaze, orderEncryptionKey },
  } = useAppContext();
  const { mainAddress } = useWalletObserver();
  const headInfo = useHydrozoaHeadInfo();
  const { mutateAsync: transact } = useTransactionWithBody();

  return useCallback(
    async (baseNonce: number) => {
      if (!headInfo || !blaze || !mainAddress || !orderEncryptionKey) return;
      const accountId = Address.fromBech32(mainAddress).getProps().paymentPart
        ?.hash;
      if (!accountId) return;

      // Fresh delegated key, clean slate.
      const ephemeral = await regenerateEphemeralWallet(mainAddress);
      const delegatedKey = Buffer.from(
        blake2b(Buffer.from(ephemeral.publicKey, "hex"), { dkLen: 28 }),
      ).toString("hex");

      const validityStart = toSeconds(Date.now() - 1_000);
      const validityEnd = validityStart + 31;
      const content = {
        tag: "UserTransaction",
        accountId,
        validityStartTime: validityStart,
        validityEndTime: validityEnd,
        actions: [
          { tag: "AddDelegatedKey", key: delegatedKey, nonce: baseNonce + 1 },
        ],
      };

      // MAIN-wallet signature: the account's own key authorises the delegate.
      const { key: coseKey, signature: coseSignature } =
        await blaze.wallet.signData(
          Address.fromBech32(mainAddress),
          Buffer.from(JSON.stringify(content)).toString("hex"),
        );

      const l2PayloadBuf = Buffer.from(
        JSON.stringify({ coseKey, coseSignature, body: content }),
      );

      const sessionKey = orderEncryptionKey.session.publicKey;
      if (sessionKey.length !== 32) throw new Error("Incorrect size for session key");
      const hkdfSalt = orderEncryptionKey.hkdfSalt;
      if (hkdfSalt.length !== 32) throw new Error("Incorrect size for hkdf salt");
      const encryptionKey = orderEncryptionKey.encryption;
      const nonce = randomBytes(12);
      const chacha = chacha20poly1305(encryptionKey, nonce);
      const enc = chacha.encrypt(compressPayload(l2PayloadBuf));
      const bytes = new Uint8Array(
        sessionKey.length + hkdfSalt.length + nonce.length + enc.length,
      );
      bytes.set(sessionKey, 0);
      bytes.set(hkdfSalt, sessionKey.length);
      bytes.set(nonce, sessionKey.length + hkdfSalt.length);
      bytes.set(enc, sessionKey.length + hkdfSalt.length + nonce.length);

      await transact({ l2Payload: bytes.toHex(), type: "transaction" });
    },
    [blaze, mainAddress, orderEncryptionKey, headInfo, transact],
  );
}
